import React from 'react';
import {
  Container,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  ViewList as ListIcon,
  ViewKanban as KanbanIcon,
  Timeline as TimelineIcon,
  Home as HomeIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PrototypeCard } from '../components/prototypes/PrototypeCard';

export const PrototypeSelector: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const prototypes = [
    {
      title: "Progress Tracker",
      description: "A client-focused view perfect for tracking tax return processing jobs through our pipeline. Shows real-time progress, ETAs, and provides communication channels for each job.",
      targetUser: "Client Portal",
      features: [
        "Real-time progress tracking",
        "Pipeline stage visualization",
        "ETA and status updates",
        "Job-level communication",
        "File exchange capabilities"
      ],
      route: "/progress",
      icon: <TimelineIcon />,
      badges: ["Client-Focused", "Progress", "Communication"],
    },
    {
      title: "Dashboard Style",
      description: "An executive-focused view perfect for managers who need quick insights and status overviews. Features visual metrics, charts, and summary cards to provide a bird's-eye view of all tax preparation activities.",
      targetUser: "Executive Overview",
      features: [
        "Visual metrics and KPI cards",
        "Interactive charts and graphs",
        "Recent activity summaries",
        "Quick action buttons",
        "Status overview widgets"
      ],
      route: "/dashboard",
      icon: <DashboardIcon />,
      badges: ["Executive", "Visual", "Analytics"],
    },
    {
      title: "List Heavy",
      description: "A data-dense interface ideal for power users who process many jobs with advanced filtering capabilities. Provides maximum information density with sophisticated search and bulk operation tools.",
      targetUser: "Data Management",
      features: [
        "Advanced filtering and search",
        "Bulk operations and actions",
        "Sortable data columns",
        "Export capabilities",
        "Detailed job information"
      ],
      route: "/jobs",
      icon: <ListIcon />,
      badges: ["Power User", "Data Dense", "Productivity"],
    },
    {
      title: "Kanban Style",
      description: "A visual workflow management system great for teams who prefer drag-and-drop interaction. Perfect for tracking job progress through different stages with intuitive visual cues.",
      targetUser: "Visual Workflow",
      features: [
        "Drag-and-drop job management",
        "Visual workflow stages",
        "Progress tracking",
        "Team collaboration",
        "Status-based organization"
      ],
      route: "/kanban",
      icon: <KanbanIcon />,
      badges: ["Visual", "Workflow", "Collaboration"],
    },
  ];

  const handleBreadcrumbNav = (path: string) => {
    navigate(path);
  };

  return (
    <Box minHeight="100vh" bgcolor="background.default">
      {/* Header Section */}
      <Box bgcolor="primary.main" color="primary.contrastText" py={6}>
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            sx={{ mb: 3, color: 'inherit' }}
            separator="›"
          >
            <Link
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleBreadcrumbNav('/progress');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <HomeIcon fontSize="small" />
              Home
            </Link>
            <Typography color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScienceIcon fontSize="small" />
              Prototypes
            </Typography>
          </Breadcrumbs>

          {/* Hero Content */}
          <Box textAlign={isMobile ? 'center' : 'left'}>
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' }
              }}
            >
              Tax Assist Prototypes
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 3, 
                opacity: 0.9,
                maxWidth: '800px',
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }
              }}
            >
              Explore three different approaches to managing tax preparation jobs. 
              Each prototype is designed for different user types and workflows.
            </Typography>
            <Chip
              label="Experimental Features"
              color="secondary"
              size="medium"
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Introduction */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 6, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
            Choose Your Preferred Interface
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            Each prototype demonstrates a different approach to organizing and interacting with tax preparation data. 
            Try all three to see which workflow best matches your needs and preferences.
          </Typography>
          <Divider />
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label="🎯 Task-Focused" size="small" variant="outlined" />
            <Chip label="📊 Data-Driven" size="small" variant="outlined" />
            <Chip label="🔄 Workflow-Oriented" size="small" variant="outlined" />
            <Chip label="📱 Mobile-Responsive" size="small" variant="outlined" />
          </Box>
        </Paper>

        {/* Prototype Cards Grid */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 4,
            mb: 6
          }}
        >
          {prototypes.map((prototype, index) => (
            <PrototypeCard key={index} {...prototype} />
          ))}
        </Box>

        {/* Additional Information */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mt: 6, 
            border: '1px solid', 
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
            About These Prototypes
          </Typography>
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Purpose:</strong> These prototypes help evaluate different user interface approaches 
                for the TaxAssist Manager platform. Each one uses the same underlying data but presents 
                it in a different way to suit various work styles.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Feedback:</strong> Your usage patterns and feedback will help determine which 
                approach works best for different user roles and scenarios.
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Features:</strong> All prototypes include the core functionality for managing 
                tax preparation jobs, but the organization and interaction patterns differ significantly.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Note:</strong> These are experimental interfaces. The final product may combine 
                elements from all three approaches based on user feedback and testing results.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};